import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Service } from "@builder-services/core/service";
import { ContextService } from "@services/context.service";
import { Me } from "@models/Me";
import { FeatureFlagConfig } from "@models/feature-flags/FeatureFlagConfig";
import { FeatureFlagsService } from "@services/feature-flags/feature-flags.service";
import { CustomOutcomeAttributesService } from "@services/http/custom-outcome-attributes.service";

export const VALIDATION_PATTERNS = {
    int: /^\d+$/,
    date: /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/,
    uuid: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
};

@Component({
    selector: "app-custom-outcome-attributes",
    templateUrl: "custom-outcome-attributes.component.html",
    styleUrls: ["custom-outcome-attributes.component.scss"],
})

export class CustomOutcomeAttributesComponent implements OnChanges {
    @Output() customOutcomeChanged = new EventEmitter<any>();
    @Output() formValidityChanged = new EventEmitter<boolean>();
    @Input() outcomeId: string;
    @Input() outcome: any;
    @Input() customClass?: string = "";
    @Input() disabled = false;

    private me: Me;
    campaignId: string;
    companyId: string;
    settingsLoaded = false;
    hasSettings = false;
    settings: any;
    disableComponent = false;
    form: FormGroup = new FormGroup({});
    isCustomOutcomeAttributesComponentVisible: boolean;

    constructor(
        private customOutcomeAttributesService: CustomOutcomeAttributesService,
        private service: Service,
        private context: ContextService,
        private readonly featureFlagsService: FeatureFlagsService,
    ) {}

    ngOnInit() {
        this.me = this.context.getMe();
        this.campaignId = this.service.campaignId;
        this.isCustomOutcomeAttributesComponentVisible = this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagConfig.CUSTOM_OUTCOME_ATTRIBUTES);

        if (this.context.hasCustomOutcomeAttributesEnabled(this.me) && this.isCustomOutcomeAttributesComponentVisible) {
            this.getCustomAttributesSettings();
        } else {
            this.disableComponent = true;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }

        if (changes.outcome && changes.outcome.currentValue) {
            this.patchFormFromOutcome();
        }
    }

    get variantSelectorName(): string {
        return this.settings?.variantSelector?.name || "outcome_type";
    }

    get selectedVariant() {
        const selectedType = this.form.get(this.variantSelectorName)?.value;
        return this.settings?.variants?.find((v) => v.type === selectedType);
    }

    private patchFormFromOutcome(): void {
        if (!this.outcome?.customAttributes) {
            return;
        }

        const outcomeType = this.outcome.customAttributes.type;
        const attributes = this.outcome.customAttributes.attributes || [];

        this.form.get(this.variantSelectorName)?.setValue(outcomeType, { emitEvent: false });

        this.updateVariantControls(outcomeType);

        attributes.forEach((attr) => {
            if (this.form.contains(attr.attributeIdentifier)) {
                this.form.get(attr.attributeIdentifier)?.setValue(attr.attributeValue, { emitEvent: false });
            }
        });

        // Emit manually, since we used { emitEvent: false }
        this.emitCustomOutcome();
    }

    getCustomAttributesSettings(): void {
        this.customOutcomeAttributesService.getSettings(this.campaignId).subscribe(
            (response) => {
                this.settings = response.data?.schema?.schema;
                this.settingsLoaded = true;

                const selectedType = this.outcome?.customAttributes?.type || this.settings?.variantSelector?.default;
                const prefillAttributes = this.outcome?.customAttributes?.attributes || [];

                this.buildForm(selectedType, prefillAttributes);
                this.updateVariantControls(selectedType);
                this.patchFormFromOutcome();

                this.form.get(this.variantSelectorName)?.valueChanges.subscribe((value) => {
                    this.updateVariantControls(value);
                });
            },
            () => {
                this.hasSettings = false;
            }
        );
    }

    private getValidatorsForAttribute(attr: any): any[] {
        const validators = [];

        if (attr.required) {
            validators.push(Validators.required);
        }
        if (attr.type === "int") {
            validators.push(Validators.pattern(VALIDATION_PATTERNS.int));
        }
        if (attr.type === "uuid") {
            validators.push(Validators.pattern(VALIDATION_PATTERNS.uuid));
        }
        if (attr.type === "date") {
            validators.push(Validators.pattern(VALIDATION_PATTERNS.date));
        }
        if (typeof attr.min === "number") {
            validators.push(Validators.min(attr.min));
        }
        if (typeof attr.max === "number") {
            validators.push(Validators.max(attr.max));
        }

        return validators;
    }

    buildForm(selectedType: string, prefillAttributes: any[] = []) {
        const controlsConfig: { [key: string]: FormControl } = {};

        controlsConfig[this.variantSelectorName] = new FormControl(selectedType);

        const variant = this.getVariantByType(selectedType);
        if (variant?.attributes) {
            variant.attributes.forEach((attr) => {
                const value = prefillAttributes.find((a) => a.attributeIdentifier === attr.name)?.attributeValue || "";
                controlsConfig[attr.name] = new FormControl(
                    value,
                    this.getValidatorsForAttribute(attr)
                );
            });
        }

        this.form = new FormGroup(controlsConfig);

        this.formValidityChanged.emit(this.form.valid);
        if (this.disabled) {
            this.form.disable();
        }

        this.form.valueChanges.subscribe(() => {
            this.emitCustomOutcome();
            this.formValidityChanged.emit(this.form.valid);
        });
    }

    updateVariantControls(selectedType: string) {
        const variant = this.getVariantByType(selectedType);
        if (!variant) {
            return;
        }

        Object.keys(this.form.controls).forEach((controlName) => {
            if (controlName !== this.variantSelectorName) {
                this.form.removeControl(controlName);
            }
        });

        const prefillAttributes =
            this.outcome?.customAttributes?.type === selectedType
                ? this.outcome?.customAttributes?.attributes || []
                : [];

        variant.attributes.forEach((attr) => {
            const value = prefillAttributes.find((a) => a.attributeIdentifier === attr.name)?.attributeValue || "";
            this.form.addControl(
                attr.name,
                new FormControl(value, this.getValidatorsForAttribute(attr))
            );
        });

        this.form.updateValueAndValidity();
    }

    getVariantByType(type: string) {
        return this.settings?.variants?.find((v) => v.type === type) || null;
    }

    emitCustomOutcome(): void {
        const outcomeType = this.form.get(this.settings?.variantSelector?.name)?.value;

        const selectedVariant = this.getVariantByType(outcomeType);
        if (!selectedVariant) {
            return;
        }

        const attributes = selectedVariant.attributes.map((attr) => {
            const control = this.form.get(attr.name);
            const rawValue = control?.value;

            let finalValue: string | number = rawValue;

            if (attr.type === "int") {
                if (rawValue !== "" && rawValue !== null && rawValue !== undefined) {
                    const parsed = Number(rawValue);
                    finalValue = isNaN(parsed) ? rawValue : parsed;
                }
            }

            return {
                attributeIdentifier: attr.name,
                attributeValue: finalValue,
            };
        });

        const output = {
            type: outcomeType,
            attributes,
        };

        this.customOutcomeChanged.emit(output);
    }
}
